import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ConfiguratorView, { type ResolvedConfig } from "../ConfiguratorView";
import { getEmbedBranding, type EmbedBranding } from "../../features/vault/vaultApi";

/** Point the page's <link rel="icon"> at a new href (creating it if needed). */
function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Public embed runtime (no auth). Resolves the broker (origin-checked) for a
 * fresh signed GLB + saved config, then hands it to the shared ConfiguratorView.
 * When served on a customer's custom domain, the browser tab title + favicon are
 * white-labeled to their brand, leaving only a small "by Jewelitt Vault" credit.
 */
export default function EmbedPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ResolvedConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [branding, setBranding] = useState<EmbedBranding | null>(null);

  // Apply white-label branding for the host this page is served on.
  useEffect(() => {
    let alive = true;
    getEmbedBranding(window.location.host).then((b) => {
      if (!alive || !b) return;
      setBranding(b);
      if (b.whiteLabel) {
        if (b.title) document.title = b.title;
        if (b.favicon) setFavicon(b.favicon);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/v1/embeds/${token}/resolve`);
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.message || "Embed unavailable");
        if (alive) setData(json.data as ResolvedConfig);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "Embed unavailable");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // Fall back to the model name in the tab when not white-labeled.
  useEffect(() => {
    if (data?.name && !branding?.whiteLabel) document.title = data.name;
  }, [data?.name, branding?.whiteLabel]);

  if (error)
    return (
      <div className="flex h-screen items-center justify-center bg-ink-950 text-ink-300">
        <p className="text-[14px]">{error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="flex h-screen items-center justify-center bg-ink-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold-400 border-t-transparent" />
      </div>
    );

  return (
    <div className="relative h-screen w-screen">
      <ConfiguratorView data={data} />
      {branding?.whiteLabel && !data.options?.removeBacklink && (
        <a
          href="https://jewelitt.com"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-2 right-3 z-40 select-none text-[10px] font-medium tracking-wide text-white/45 transition-colors hover:text-white/70"
        >
          {branding.byline}
        </a>
      )}
    </div>
  );
}
